import React, { useRef } from "react";
import DateField from "../DateField";
import Dropdown from "../Dropdown";
import InputField from "../InputField";
import MailboxWishesSection from "./MailboxWishesSection";
import {
  DEFAULT_DELETION_DAYS,
  defaultDeletionDate,
  userOptionLabel,
} from "./offboardingFormData";

/**
 * The "Offboard user" form (epic #288): who is leaving, when the account should be disabled and
 * deleted, what happens to their computer, and the optional mailbox follow-ups. The form is
 * controlled — it never mutates `value`, it hands a new object to `onChange`.
 *
 * @param {object} props
 * @param {object[]} props.users - The tenant's users; deleted ones are not offered
 * @param {object} props.settings - SCO form settings (help text, deletion default days)
 * @param {object} props.value - The form value, shaped like EMPTY_OFFBOARDING
 * @param {(next: object) => void} props.onChange - Receives the updated value
 */
const OffboardingForm = ({ users = [], settings = {}, value, onChange }) => {
  // Once the orderer types their own deletion date, a later end-date change must not overwrite it.
  const deletionDateEdited = useRef(false);

  const deletionDays = settings.offboardingDeletionDefaultDays ?? DEFAULT_DELETION_DAYS;
  const selectableUsers = users.filter((user) => !user.isDeleted);
  const selectedUser = selectableUsers.find((user) => user.userPublicId === value.userPublicId);
  const otherUsers = selectableUsers.filter((user) => user.userPublicId !== value.userPublicId);

  const handleUserSelect = (label) => {
    const user = selectableUsers.find((candidate) => userOptionLabel(candidate) === label);
    if (!user) return;

    // Colleagues chosen for the mailbox may be the person now being offboarded.
    onChange({
      ...value,
      userPublicId: user.userPublicId,
      sharedMailboxAccessUserPublicIds: (value.sharedMailboxAccessUserPublicIds || []).filter(
        (publicId) => publicId !== user.userPublicId
      ),
      forwardToUserPublicId:
        value.forwardToUserPublicId === user.userPublicId ? "" : value.forwardToUserPublicId,
    });
  };

  const handleEndDateChange = (endDate) => {
    onChange({
      ...value,
      endDate,
      deletionDate: deletionDateEdited.current
        ? value.deletionDate
        : defaultDeletionDate(endDate, deletionDays),
    });
  };

  const handleDeletionDateChange = (deletionDate) => {
    deletionDateEdited.current = true;
    onChange({ ...value, deletionDate });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <Dropdown
        label="User"
        value={selectedUser ? selectedUser.displayName || selectedUser.userPrincipalName : ""}
        options={selectableUsers.map(userOptionLabel)}
        onSelect={handleUserSelect}
        searchable={true}
      />

      <DateField label="End date" value={value.endDate || ""} onChange={handleEndDateChange} />

      <DateField
        label="Deletion date"
        value={value.deletionDate || ""}
        onChange={handleDeletionDateChange}
      />

      {settings.offboardingHelpText && (
        <p className="text-sm text-gray-600">{settings.offboardingHelpText}</p>
      )}

      <InputField
        label="What should happen to the existing computer?"
        value={value.computerDisposition || ""}
        onChange={(event) => onChange({ ...value, computerDisposition: event.target.value })}
        placeholder="For example: wipe and reuse, return to Diflex, keep"
        type="text"
      />

      <MailboxWishesSection value={value} otherUsers={otherUsers} onChange={onChange} />

      <InputField
        label="Comment"
        value={value.comment || ""}
        onChange={(event) => onChange({ ...value, comment: event.target.value })}
        placeholder="Anything Diflex should know about this offboarding"
        type="text-area"
      />
    </div>
  );
};

export default OffboardingForm;
